import { QueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useSetAtom } from 'jotai'
import { HubConnection, HubConnectionState } from '@microsoft/signalr'
import { connectionHub } from './connectionHub'
import { invalidateAllQueries, Topic, applyLatestToMeasurements, appendPointToTrends } from './realtimeUtils'
import { addSocketErrorMessage, connectionStatusAtom, removeSocketErrorMessage } from '@/core/store/globalStore'
import { QueryKeys } from '@/core/models/enum'
import { InstalledSensorsDataDto, OmniaTimeSeriesDto, TrendDto } from '@/api_generated/api'

const offlineErrorMessage = 'Realtime updates in IIOT App could be outdated. Click on refresh to get live updates.'

// Sensor messages arrive in bursts; the tag list only needs one refresh per burst.
const FILTERED_TAGS_REFRESH_MS = 5000

// One channel per tab: the connection callbacks outlive the renders that registered them.
let networkStatusChannel: BroadcastChannel | null = null
let filteredTagsRefresh: number | undefined

function postNetworkStatus(status: 'online' | 'offline'): void {
    networkStatusChannel ??= new BroadcastChannel('network-status')
    networkStatusChannel.postMessage({ type: 'NETWORK_STATUS', status })
}

function scheduleFilteredTagsRefresh(queryClient: QueryClient): void {
    if (filteredTagsRefresh !== undefined) return

    filteredTagsRefresh = window.setTimeout(() => {
        filteredTagsRefresh = undefined
        queryClient.invalidateQueries({ queryKey: [QueryKeys.FilteredTags] })
    }, FILTERED_TAGS_REFRESH_MS)
}

export function useRealtimeService(queryClient: QueryClient): void {
    const setConnectionStatus = useSetAtom(connectionStatusAtom)
    const [hubConnection, setHubConnection] = useState<HubConnection | null>(null)

    useEffect(() => {
        if (!hubConnection) {
            const setUpSignalRConnection = async () => {
                const connection = connectionHub()

                connection.onreconnecting(() => {
                    setConnectionStatus(HubConnectionState.Reconnecting)
                    postNetworkStatus('offline')
                })

                connection.onreconnected(() => {
                    removeSocketErrorMessage(offlineErrorMessage)
                    invalidateAllQueries(queryClient)
                    setConnectionStatus(HubConnectionState.Connected)
                    postNetworkStatus('online')
                })

                connection.onclose(() => {
                    addSocketErrorMessage(offlineErrorMessage)
                    setConnectionStatus(HubConnectionState.Disconnected)
                })

                connection.on(Topic.notifySensor, (message: any) => {
                    scheduleFilteredTagsRefresh(queryClient)

                    const wrapper = message?.arguments?.[0] ?? message?.payload ?? message?.data ?? message
                    const dto = wrapper?.sensor ?? wrapper?.sensorData ?? wrapper

                    if (!dto?.sensorDbKey) return

                    const queryKey = [QueryKeys.OnboardedSensors] as const
                    const cachedLists = queryClient.getQueriesData<InstalledSensorsDataDto[]>({ queryKey })

                    if (cachedLists.length === 0) {
                        queryClient.invalidateQueries({ queryKey })
                        return
                    }

                    cachedLists.forEach(([cachedKey, cachedData]) => {
                        // The list is cached per selected plant, so skip caches this sensor does not belong to.
                        const cachedPlant = cachedKey[1] as string | undefined
                        if (cachedPlant && dto.plant !== cachedPlant) return

                        // Never seed the cache from a single message: the query uses refetchOnMount:false,
                        // so a seeded partial list would stop the full-list fetch from ever running.
                        if (cachedData === undefined) {
                            queryClient.invalidateQueries({ queryKey: cachedKey })
                            return
                        }

                        queryClient.setQueryData<InstalledSensorsDataDto[]>(cachedKey, (oldData = []) => {
                            const existing = oldData.some((sensor) => sensor.sensorDbKey === dto.sensorDbKey)

                            if (!existing) {
                                return [...oldData, dto]
                            }

                            return oldData.map((sensor) => {
                                if (sensor.sensorDbKey !== dto.sensorDbKey) {
                                    return sensor
                                }
                                return {
                                    ...sensor,
                                    ...dto,
                                    hasAccess: sensor.hasAccess,
                                }
                            })
                        })
                    })
                })

                connection.on('notifyTimeSeries', (message: any) => {
                    const wrapper = message?.arguments?.[0] ?? message?.payload ?? message?.data ?? message
                    const dto = wrapper?.timeSeriesValues ?? wrapper

                    if (!dto?.timeseriesTableDbKey) return
                    if (!dto?.lastReceived) return
                    if (typeof dto?.value !== 'number' || !Number.isFinite(dto.value)) return

                    const id = String(dto.timeseriesTableDbKey)
                    const newPoint = {
                        time: dto.lastReceived as string,
                        value: dto.value as number,
                    }

                    // Prefix match: the trend page keys its queries on [Trends, id, fromDate].
                    const trendCaches = queryClient.getQueriesData<TrendDto[]>({
                        queryKey: [QueryKeys.Trends, id],
                    })
                    for (const [cacheKey, cacheValue] of trendCaches) {
                        const next = appendPointToTrends(cacheValue, newPoint)
                        if (next !== cacheValue) {
                            queryClient.setQueryData(cacheKey, next)
                        }
                    }

                    const measurementsUpdate = {
                        timeseriesTableDbKey: id,
                        value: dto.value as number,
                        lastReceived: dto.lastReceived as string,
                    }
                    const measurementCaches = queryClient.getQueriesData<OmniaTimeSeriesDto[]>({
                        queryKey: [QueryKeys.Measurements],
                    })
                    for (const [cacheKey, cacheValue] of measurementCaches) {
                        const next = applyLatestToMeasurements(cacheValue, measurementsUpdate)
                        if (next !== cacheValue) {
                            queryClient.setQueryData(cacheKey, next)
                        }
                    }
                })

                connection.on('notifyConfirmation', (message: any) => {
                    const dto = message?.arguments?.[0] ?? message?.payload ?? message?.data ?? message

                    if (!dto?.sensorDbKey) return

                    const id = String(dto.sensorDbKey)
                    const queryKey = [QueryKeys.Configurations, id] as const

                    queryClient.invalidateQueries({ queryKey: queryKey })
                })

                return connection
            }

            setUpSignalRConnection()
                .then((con) => {
                    setHubConnection(con)
                    setConnectionStatus(HubConnectionState.Connected)
                })
                .catch((e: Error) => {
                    console.log(`Failed to set up SignalR connection, with the following message: ${e.message}`)
                    setConnectionStatus(HubConnectionState.Disconnected)
                    addSocketErrorMessage(offlineErrorMessage)
                })
        }

        if (hubConnection && hubConnection.state !== HubConnectionState.Connected) {
            hubConnection
                .start()
                .then(() => {
                    setConnectionStatus(HubConnectionState.Connected)
                })
                .catch((e: Error) => {
                    console.log(`Failed to set up SignalR connection, with the following message: ${e}`)
                    setConnectionStatus(HubConnectionState.Disconnected)
                    addSocketErrorMessage(offlineErrorMessage)
                })
        }

        return () => {
            hubConnection?.stop()
        }
    }, [hubConnection, queryClient, setConnectionStatus])
}
